import { ORG_ADMIN_GATE_REDIRECT_TIMEOUT_MS, ROUTE_PATHS } from '@/constants';
import type { Role } from '@/types';
import { useEffect, useState } from 'react';

import { useRouteProtection } from './useRouteProtection';

export type OrgAdminGateStatus = 'pending' | 'auth-error' | 'unauthenticated' | 'denied' | 'authorized';

export interface OrgAdminRenderGate {
  status: OrgAdminGateStatus;
  hasAccess: boolean;
}

/** Shared render gate for org-admin-protected pages, and the bounded watchdog that escapes a
 *  no-access state whose redirect was silently dropped. Each status rung, and why `unauthenticated`
 *  is not folded into `denied`, is documented in docs/ROUTE_PROTECTION.md (POR-485, issues #396/#397). */
export function useOrgAdminRenderGate(requiredRoles: Role[], redirectTo = '/unauthorized'): OrgAdminRenderGate {
  const { hasAccess, isResolving, isAuthError, isUnauthenticated } = useRouteProtection(requiredRoles, redirectTo);
  const [watchdogTripped, setWatchdogTripped] = useState(false);

  const awaitingRedirect = !isResolving && !isAuthError && !hasAccess;

  useEffect(() => {
    if (!awaitingRedirect) {
      // oxlint-disable-next-line set-state-in-effect -- resets the watchdog latch once the no-access wait ends (useInitialAuthGate pattern)
      setWatchdogTripped(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      console.error(
        '[useOrgAdminRenderGate] redirect away from a resolved no-access state never landed within the watchdog window; escalating to the auth-error surface',
        {
          pathname: window.location.pathname,
          // The awaited destination, not this hook's param: a no-session state is waiting on the
          // root beforeLoad's /login, which `redirectTo` would misreport as /unauthorized.
          awaitedRedirect: isUnauthenticated ? ROUTE_PATHS.LOGIN : redirectTo,
          timeoutMs: ORG_ADMIN_GATE_REDIRECT_TIMEOUT_MS,
        },
      );
      setWatchdogTripped(true);
    }, ORG_ADMIN_GATE_REDIRECT_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // A flip either way — sign-in turning a no-session load into a role denial, or expiry turning a
    // denial into one — changes which redirect is awaited, so the pre-trip window restarts.
  }, [awaitingRedirect, isUnauthenticated, redirectTo]);

  // hasAccess/isResolving/isAuthError are checked ahead of the watchdog latch so a stale trip
  // (the reset effect above runs post-commit) can't paint one extra auth-error frame once access
  // is restored or auth starts resolving again.
  const status: OrgAdminGateStatus = isResolving
    ? 'pending'
    : isAuthError
      ? 'auth-error'
      : hasAccess
        ? 'authorized'
        : watchdogTripped
          ? 'auth-error'
          : isUnauthenticated
            ? 'unauthenticated'
            : 'denied';

  return { status, hasAccess };
}
